import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import { formatDateTime } from '../dateTimeUtils';
import {
  DelayEventKind,
  EVENT_KIND_META,
} from '../nodes/delayAction/delayUntilEventModel';
import styles from './RunNodeAttemptsDrawer.module.css';

type Json = Record<string, unknown>;

const isRecord = (value: unknown): value is Json =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const asString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

/** One event card the Delay waited for. */
interface WaitedCard {
  kind: string;
  id: string;
}

/** What a Delay until event recorded on its step. Fields are read defensively: it is
 * node-owned JSON, and a stage that failed to write leaves its fields out. */
export interface DelayWaitDetail {
  outcome: 'waiting' | 'event' | 'timed_out';
  waitingFor: WaitedCard[];
  waitingSince: string | null;
  deadline: string | null;
  endedAt: string | null;
  matched: WaitedCard | null;
  /** Null until "If nothing happens" was applied. */
  ifNothingHappens: {
    endRun: boolean;
    notifiedOwner: boolean;
    notifiedOthers: boolean;
  } | null;
}

function parseCard(raw: unknown): WaitedCard | null {
  if (!isRecord(raw)) return null;
  const kind = asString(raw.kind);
  const id = asString(raw.id);
  return kind !== null && id !== null ? { kind, id } : null;
}

/** The recorded detail, or null when it is not one a Delay until event wrote. */
export function parseDelayWaitDetail(detail: Json): DelayWaitDetail | null {
  const outcome = detail.outcome;
  if (outcome !== 'waiting' && outcome !== 'event' && outcome !== 'timed_out') {
    return null;
  }
  const rule = isRecord(detail.if_nothing_happens)
    ? detail.if_nothing_happens
    : null;
  return {
    outcome,
    waitingFor: Array.isArray(detail.waiting_for)
      ? detail.waiting_for
          .map(parseCard)
          .filter((card): card is WaitedCard => card !== null)
      : [],
    waitingSince: asString(detail.waiting_since),
    deadline: asString(detail.deadline),
    endedAt: asString(detail.ended_at),
    matched: parseCard(detail.matched),
    ifNothingHappens:
      rule === null
        ? null
        : {
            endRun: rule.end_run === true,
            notifiedOwner: rule.notified_owner === true,
            notifiedOthers: rule.notified_others === true,
          },
  };
}

const isEventKind = (kind: string): kind is DelayEventKind =>
  (Object.values(DelayEventKind) as string[]).includes(kind);

/** The editor's name for an event kind. A kind this build does not know reads as its own
 * key spelled out ("note_added" -> "Note added"). */
function kindLabel(kind: string): string {
  if (isEventKind(kind)) return EVENT_KIND_META[kind].label;
  const spelled = kind.replace(/_/g, ' ');
  return spelled.charAt(0).toUpperCase() + spelled.slice(1);
}

/** A card's name, with its place among the cards of the same kind when there are several. */
function cardLabel(card: WaitedCard, cards: WaitedCard[]): string {
  const label = kindLabel(card.kind);
  const sameKind = cards.filter((other) => other.kind === card.kind);
  if (sameKind.length < 2) return label;
  const index = sameKind.findIndex((other) => other.id === card.id);
  return `${label} (card ${index + 1})`;
}

/** "1 min 45 s": the largest unit that is not zero and the one below it when that is not zero
 * either, so a wait of a day and five seconds reads "1 d", never "1 d 5 s". */
export function formatWaited(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const units: [number, string][] = [
    [Math.floor(totalSeconds / 86400), 'd'],
    [Math.floor((totalSeconds % 86400) / 3600), 'h'],
    [Math.floor((totalSeconds % 3600) / 60), 'min'],
    [totalSeconds % 60, 's'],
  ];
  const first = units.findIndex(([amount]) => amount > 0);
  if (first === -1) return '0 s';
  return units
    .slice(first, first + 2)
    .filter(([amount], position) => position === 0 || amount > 0)
    .map(([amount, unit]) => `${amount} ${unit}`)
    .join(' ');
}

function waitedMs(detail: DelayWaitDetail): number | null {
  if (detail.waitingSince === null || detail.endedAt === null) return null;
  const waited =
    new Date(detail.endedAt).getTime() -
    new Date(detail.waitingSince).getTime();
  return Number.isNaN(waited) ? null : waited;
}

function heading(detail: DelayWaitDetail, stillWaiting: boolean): string {
  if (detail.outcome === 'event') return 'An event happened';
  if (detail.outcome === 'timed_out') return 'Nothing happened in time';
  return stillWaiting ? 'Waiting for an event' : 'Stopped waiting';
}

function ifNothingHappensText(
  rule: NonNullable<DelayWaitDetail['ifNothingHappens']>,
  stepFailed: boolean
): string[] {
  // The note is written before the run resumes. A Delay step that then failed is a run that could
  // not go on, whatever the rule said.
  const lines = [
    rule.endRun
      ? 'The run ended here.'
      : stepFailed
        ? 'The run could not continue past the Delay.'
        : 'The run continued past the Delay.',
  ];
  if (rule.notifiedOwner && rule.notifiedOthers) {
    lines.push('Notified the automation owner and other people.');
  } else if (rule.notifiedOwner) {
    lines.push('Notified the automation owner.');
  } else if (rule.notifiedOthers) {
    lines.push('Notified other people.');
  }
  return lines;
}

function Field({ label, children }: { label: string; children: string }) {
  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue}>{children}</div>
    </div>
  );
}

/**
 * What a Delay until event is waiting for or why it stopped: the events it waited for, which one
 * happened or that the deadline passed, how long it waited, and what "If nothing happens" did.
 * `stillWaiting` is whether the Delay's step is still running, which tells a wait that is going on
 * from one that was recorded as waiting and then cut short.
 */
export function DelayWaitCard({
  detail,
  stillWaiting,
  stepFailed = false,
}: {
  detail: DelayWaitDetail;
  stillWaiting: boolean;
  stepFailed?: boolean;
}) {
  const { dateFormat } = useUserDateTimeFormat();
  const when = (iso: string) => formatDateTime(iso, dateFormat);
  const waited = waitedMs(detail);
  const live = detail.outcome === 'waiting' && stillWaiting;

  return (
    <div className={styles.attemptCard}>
      <div className={styles.attemptHeader}>
        <span className={styles.attemptTitle}>
          {heading(detail, stillWaiting)}
        </span>
      </div>

      {detail.matched && (
        <Field label='Event'>
          {cardLabel(detail.matched, detail.waitingFor)}
        </Field>
      )}
      {detail.waitingFor.length > 0 && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>
            {live ? 'Waiting for' : 'Was waiting for'}
          </div>
          {detail.waitingFor.map((card) => (
            <div key={card.id} className={styles.fieldValue}>
              {cardLabel(card, detail.waitingFor)}
            </div>
          ))}
        </div>
      )}
      {detail.waitingSince && (
        <Field label='Waiting since'>{when(detail.waitingSince)}</Field>
      )}
      {detail.deadline && (
        <Field label={live ? 'Until' : 'Deadline was'}>
          {when(detail.deadline)}
        </Field>
      )}
      {waited !== null && <Field label='Waited'>{formatWaited(waited)}</Field>}
      {detail.ifNothingHappens && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>If nothing happens</div>
          {ifNothingHappensText(detail.ifNothingHappens, stepFailed).map(
            (line) => (
              <div key={line} className={styles.fieldValue}>
                {line}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
